import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import logoColors from "@/assets/logo-colors.png";
import useTranslation from "@babackapp/translations/use-translation";
import { useAuth } from "@babackapp/auth-client/hooks";
import {
  VerificationLoading,
  VerificationSuccess,
  VerificationError,
  VerificationInvalid,
  ResendForm,
  ResendSuccess
} from "./components";


type VerificationStatus = "loading" | "success" | "error" | "invalid";

export default function VerifyEmailPage() {
  const t = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<VerificationStatus>("loading");
  const [showResendForm, setShowResendForm] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const hasVerified = useRef(false);

  const token = searchParams.get("token");

  useEffect(() => {
    if (hasVerified.current) {
      return;
    }
    hasVerified.current = true;
    const verifyEmail = async () => {
      if (!token) {
        setStatus("invalid");
        return;
      }

      try {
        await auth.verifyEmail(token);
        setStatus("success");
      } catch (error: unknown) {
        console.error("Email verification failed:", error);
        setStatus("error");
      }
    };

    verifyEmail();
  }, [token]);

  const handleBackToSignIn = () => {
    navigate("/auth/sign-in");
  };

  const handleResendVerification = () => {
    setShowResendForm(true);
    setResendSuccess(false);
  };

  const handleSubmitResend = async (email: string) => {
    setIsResending(true);
    try {
      await auth.resendVerification(email);
      setResendSuccess(true);
      setShowResendForm(false);
    } catch (error) {
      console.error("Failed to resend verification:", error);
    } finally {
      setIsResending(false);
    }
  };

  const handleCancelResend = () => {
    setShowResendForm(false);
    setResendSuccess(false);
  };

  const renderContent = () => {
    switch (status) {
      case "loading":
        return <VerificationLoading />;

      case "success":
        return <VerificationSuccess onBackToSignIn={handleBackToSignIn} />;

      case "error":
        if (resendSuccess) {
          return <ResendSuccess onBackToSignIn={handleBackToSignIn} />;
        }

        if (showResendForm) {
          return (
            <ResendForm
              onSubmit={handleSubmitResend}
              onCancel={handleCancelResend}
              isLoading={isResending}
            />
          );
        }

        return (
          <VerificationError
            error={auth.error?.message || undefined}
            onResendVerification={handleResendVerification}
            onBackToSignIn={handleBackToSignIn}
          />
        );

      case "invalid":
        return <VerificationInvalid onBackToSignIn={handleBackToSignIn} />;

      default:
        return null;
    }
  };

  return (
    <div className="min-h-full flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img className="mx-auto h-12 w-auto" src={logoColors} alt="Baback" />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
          {t("verifyEmail.title")}
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
